import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useStore } from 'slab/hooks/useStore'

import { ENABLED_OPTION_SELECTOR, SELECT_EVENT_NAME, SELECTED_OPTION_SELECTOR } from './constants'
import { HeadlessListboxContext } from './context'
import { HeadlessListboxControl } from './Control'
import { HeadlessListboxList } from './List'
import { HeadlessListboxOption } from './Option'

interface HeadlessListboxProps {
  children: React.ReactNode;
}

/**
 * Allows a callback to be scheduled to run in the next layout effect.
 */
function useScheduledLayoutEffect () {
  const [state, setState] = useState({})

  const callbacks = useRef(new Map<string, () => void>())
  const schedule = useCallback((id: string, callback: () => void) => {
    callbacks.current.set(id, callback)

    setState({})
  }, [])

  useLayoutEffect(() => {
    callbacks.current.forEach((callback) => callback())
    callbacks.current.clear()
  }, [state])

  return schedule
}

function findNextSibling (element: Element, selector: string) {
  let sibling = element.nextElementSibling

  while (sibling) {
    if (sibling.matches(selector)) return sibling
    sibling = sibling.nextElementSibling
  }

  return null
}

function findPreviousSibling (element: Element, selector: string) {
  let sibling = element.previousElementSibling

  while (sibling) {
    if (sibling.matches(selector)) return sibling
    sibling = sibling.previousElementSibling
  }

  return null
}

/**
 * The `<HeadlessListbox>` component is a generic, unstyled listbox component
 * that can be used to create select menus, comboboxes, and other components
 * that use a roving focus pattern to navigate and select options.
 */
export function HeadlessListbox ({ children }: HeadlessListboxProps) {
  /**
   * We're using a custom store instead of React state to manage the selected
   * option without the performance overhead of rerendering every option when
   * the state changes. It also allows us to read from the state at any time
   * without needing to worry about stale closures from hooks like `useEffect`
   * and without breaking the referential equality of `useCallback` callbacks.
   */
  const store = useStore<{ selectedOption: string | null }>({
    selectedOption: null,
  })
  const schedule = useScheduledLayoutEffect()

  const controlRef = useRef<HTMLElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const listId = useId()

  const getAllOptions = useCallback(() => {
    return listRef.current?.querySelectorAll(ENABLED_OPTION_SELECTOR) || []
  }, [])

  const selectOption = useCallback(
    (id: string) => {
      store.setState({ selectedOption: id })
    },
    [store]
  )

  const selectDefaultOption = useCallback(() => {
    const options = getAllOptions()

    store.setState({ selectedOption: options[0]?.id || null })
  }, [store])

  /**
   * A callback used by the option components to "register" and "unregister"
   * themselves when they mount and unmount. Currently used to determine if and
   * when we need to select a default option.
   */
  const registerOption = useCallback((id: string) => {
    /**
     * We need to select a default option when the dropdown list first renders.
     * We also need to select a default option if the dropdown was already
     * rendered but previously displayed no options (e.g. it only rendered a
     * loading or empty state). We can handle both cases by checking the
     * `selectedOption` state anytime an option mounts.
     */
    if (store.snapshot().selectedOption === null) {
      schedule('defaultOption', () => selectDefaultOption())
    }

    return () => {
      const freshSelectedOption = store.snapshot().selectedOption

      /**
       * If the previously selected option was filtered out we want to fallback
       * to the default option (i.e. the first option in the list). We need
       * to do that in the next layout effect to wait until the previous option
       * actually gone from the DOM before finding the default option.
       */
      if (freshSelectedOption === id) {
        schedule('defaultOption', () => selectDefaultOption())
      }
    }
  }, [])

  useEffect(() => {
    const control = controlRef.current

    if (!control) return

    function getSelectedOption () {
      return listRef.current?.querySelector(SELECTED_OPTION_SELECTOR) || null
    }

    function getFirstOption () {
      const allOptions = getAllOptions()

      return allOptions[0]
    }

    function getLastOption () {
      const allOptions = getAllOptions()

      return allOptions[allOptions.length - 1]
    }

    function getNextOption () {
      const currentOption = getSelectedOption()

      return (
        currentOption && findNextSibling(currentOption, ENABLED_OPTION_SELECTOR)
      )
    }

    function getPreviousOption () {
      const currentOption = getSelectedOption()

      return (
        currentOption &&
        findPreviousSibling(currentOption, ENABLED_OPTION_SELECTOR)
      )
    }

    function selectAndScrollTo (option: Element | null) {
      if (option) {
        store.setState({ selectedOption: option.id })
        schedule('scrollToOption', () => {
          option.scrollIntoView({ block: 'nearest' })
        })
      }
    }

    function handleKeyDown (event: KeyboardEvent) {
      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          selectAndScrollTo(getNextOption())
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          selectAndScrollTo(getPreviousOption())
          break
        }
        case 'Home': {
          event.preventDefault()
          selectAndScrollTo(getFirstOption())
          break
        }
        case 'End': {
          event.preventDefault()
          selectAndScrollTo(getLastOption())
          break
        }
        case 'Enter': {
          event.preventDefault()
          const selected = getSelectedOption()
          if (selected) {
            selected.dispatchEvent(new Event(SELECT_EVENT_NAME))
          }
          break
        }
      }
    }

    control.addEventListener('keydown', handleKeyDown)

    return () => {
      control.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      controlRef,
      listId,
      listRef,
      registerOption,
      selectOption,
      store,
    }),
    [listId, registerOption, selectOption, store]
  )

  return (
    <>
      <HeadlessListboxContext.Provider value={contextValue}>
        {children}
      </HeadlessListboxContext.Provider>
    </>
  )
}

HeadlessListbox.Control = HeadlessListboxControl
HeadlessListbox.List = HeadlessListboxList
HeadlessListbox.Option = HeadlessListboxOption
